import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Layers, Upload, Download, AlertCircle, Wallet } from 'lucide-react';
import WebApp from '@twa-dev/sdk';
import { pluralCredits, type UniqJob, type UniqJobsListResponse } from '@masha/shared';
import { api, ApiError } from '../lib/api';
import { haptic } from '../lib/tma';
import { isActiveJob, readVideoDuration, uniqBadgeLabel, uniqStatusText } from '../lib/uniq';
import { Badge, Button, Card, ProgressBar, Spinner, Toast } from '../components/ui';
import { HowItWorks } from '../components/HowItWorks';
import { HOW_IT_WORKS, uniqNote } from '../lib/howItWorksCopy';

const POLL_MS = 4000;

function formatMb(bytes: number) {
  return `${Math.round(bytes / 1_048_576)} МБ`;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function UniqPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [toast, setToast] = useState<{
    tone: 'success' | 'destructive' | 'brand';
    text: string;
  } | null>(null);

  const jobsQuery = useQuery<UniqJobsListResponse>({
    queryKey: ['uniq-jobs'],
    queryFn: () => api.getUniqJobs(),
    refetchInterval: (q) => (q.state.data?.jobs.some(isActiveJob) ? POLL_MS : false),
  });

  const data = jobsQuery.data;
  const cfg = data?.config;
  const pricing = data?.pricing;

  function showToast(tone: 'success' | 'destructive' | 'brand', text: string) {
    setToast({ tone, text });
    setTimeout(() => setToast(null), 3500);
  }

  async function handleFile(file: File) {
    if (!cfg) return;
    if (file.size > cfg.maxBytes) {
      haptic('error');
      showToast('destructive', `Файл больше ${formatMb(cfg.maxBytes)}`);
      return;
    }
    const duration = await readVideoDuration(file);
    if (duration !== null && duration > cfg.maxDurationSec) {
      haptic('error');
      showToast('destructive', `Ролик длиннее ${cfg.maxDurationSec} секунд`);
      return;
    }

    setUploadPct(0);
    try {
      await api.uploadUniqVideo(file, setUploadPct);
      haptic('success');
      showToast('success', 'Загружено. Обработка займёт несколько минут');
      await qc.invalidateQueries({ queryKey: ['uniq-jobs'] });
    } catch (err) {
      haptic('error');
      const code = err instanceof ApiError ? err.code : 'UNKNOWN';
      let text = 'Не удалось загрузить, попробуй ещё раз';
      if (code === 'UNIQ_TOO_LARGE') text = `Файл больше ${formatMb(cfg.maxBytes)}`;
      else if (code === 'UNIQ_NOT_VIDEO') text = 'Это не видеофайл';
      else if (code === 'INSUFFICIENT_CREDITS') text = 'Не хватает кредитов';
      else if (code === 'RATE_LIMITED') text = 'Слишком много загрузок, подожди немного';
      showToast('destructive', text);
      await qc.invalidateQueries({ queryKey: ['uniq-jobs'] });
    } finally {
      setUploadPct(null);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  const uploading = uploadPct !== null;
  const turboLeft = pricing?.turboFreeLeftToday ?? 0;
  const canPay = pricing ? turboLeft > 0 || pricing.balance >= pricing.costCredits : false;

  return (
    <div className="min-h-screen p-4 max-w-lg mx-auto pb-12 safe-top safe-bottom">
      <header className="flex items-center justify-between mb-4">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm text-tg-hint hover:text-tg-text transition-colors"
        >
          <ArrowLeft size={16} />
          Назад
        </Link>
        <h1 className="text-lg font-semibold">Дубликатор</h1>
        <span className="w-12" />
      </header>

      <HowItWorks
        id="uniq"
        steps={HOW_IT_WORKS.uniq.steps}
        note={uniqNote(pricing?.costCredits ?? null)}
        className="mb-4"
      />

      <Card variant="elevated" padding="lg" className="mb-4">
        <div className="flex items-start gap-3 mb-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-brand-muted text-brand flex items-center justify-center">
            <Layers size={20} />
          </div>
          <div className="min-w-0">
            <h2 className="text-base font-semibold mb-1">
              {cfg?.copies ?? 5} копий из одного ролика
            </h2>
            <p className="text-sm text-tg-hint leading-snug">
              Для зрителя одинаковые, для алгоритмов соцсетей разные файлы.
            </p>
          </div>
        </div>
        <p className="text-xs text-tg-hint leading-snug mb-3">
          Помогает пройти простые проверки на дубли. Гарантий охватов нет.
        </p>

        {pricing && (
          <p className="text-xs text-tg-hint tabular-nums mb-3">
            {turboLeft > 0
              ? `Турбо: бесплатных роликов сегодня — ${turboLeft}`
              : `Ролик стоит ${pricing.costCredits} ${pluralCredits(pricing.costCredits)} · у тебя ${pricing.balance}`}
          </p>
        )}

        {uploading ? (
          <div>
            <ProgressBar value={uploadPct ?? 0} ariaLabel="Загрузка видео" className="mb-2" />
            <p className="text-xs text-tg-hint tabular-nums">Загружаем… {uploadPct}%</p>
          </div>
        ) : pricing && !canPay ? (
          <Button fullWidth leftIcon={<Wallet size={18} />} onClick={() => navigate('/balance')}>
            Пополнить
          </Button>
        ) : (
          <Button
            fullWidth
            leftIcon={<Upload size={18} />}
            disabled={!cfg}
            onClick={() => inputRef.current?.click()}
          >
            Выбрать видео
          </Button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="video/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void handleFile(f);
          }}
        />
        {cfg && (
          <p className="text-[11px] text-tg-hint mt-2">
            До {formatMb(cfg.maxBytes)} и {cfg.maxDurationSec} секунд · mp4, mov
          </p>
        )}
      </Card>

      {jobsQuery.isLoading && (
        <div className="flex flex-col items-center py-8">
          <Spinner />
        </div>
      )}

      {jobsQuery.isError && (
        <div className="text-center py-6">
          <p className="text-sm text-tg-hint mb-3">Не удалось загрузить список</p>
          <Button variant="secondary" onClick={() => jobsQuery.refetch()}>
            Повторить
          </Button>
        </div>
      )}

      {data && data.jobs.length > 0 && cfg && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-tg-hint px-1">Последние задачи</h3>
          {data.jobs.map((job) => (
            <JobCard key={job.id} job={job} maxDurationSec={cfg.maxDurationSec} />
          ))}
        </div>
      )}

      {toast && (
        <Toast tone={toast.tone} placement="fixed">
          <span className="inline-flex items-center gap-2">
            {toast.tone === 'destructive' && <AlertCircle size={16} />}
            {toast.text}
          </span>
        </Toast>
      )}
    </div>
  );
}

function JobCard({ job, maxDurationSec }: { job: UniqJob; maxDurationSec: number }) {
  const tone =
    job.status === 'done'
      ? 'success'
      : job.status === 'failed'
        ? 'destructive'
        : job.status === 'expired'
          ? 'neutral'
          : 'brand';
  const pct =
    job.status === 'processing' ? Math.round((job.copiesDone / job.copiesTotal) * 100) : null;

  return (
    <Card variant="surface" padding="md">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{job.sourceName ?? 'Видео'}</p>
          <p className="text-xs text-tg-hint tabular-nums">
            {formatDate(job.createdAt)}
            {job.durationSec ? ` · ${job.durationSec} с` : ''}
          </p>
        </div>
        <Badge tone={tone}>{uniqBadgeLabel(job)}</Badge>
      </div>

      <p className="text-xs text-tg-hint mb-2">{uniqStatusText(job, maxDurationSec)}</p>
      {pct !== null && <ProgressBar value={pct} className="mb-2" />}

      {job.status === 'done' && job.outputUrls.length > 0 && (
        <div className="grid grid-cols-5 gap-2">
          {job.outputUrls.map((url, i) => (
            <button
              key={url}
              type="button"
              onClick={() => {
                haptic('light');
                // openLink needs an absolute URL; outputUrls are site-relative.
                WebApp.openLink(new URL(url, window.location.origin).toString());
              }}
              className="inline-flex items-center justify-center gap-1 h-9 rounded-lg bg-tg-bg text-xs font-medium transition-opacity active:opacity-70"
            >
              <Download size={12} />
              {i + 1}
            </button>
          ))}
        </div>
      )}
    </Card>
  );
}
